import { NetworkBasePopUp } from '../NetworkBasePopUp';
import { GzRecoveryManagerWebComponent } from './component/GzRecoveryManagerWebComponent';
import { translateFn } from '../../../../utils/translator.helper';
import { GzDialog } from '../../../../ui/gzDialog/component/GzDialog';
import { ConsoleRequest, DataProviderEntity, DataProviderType } from '../../../../utils/request';
import { VolumeDetails } from './component/types';
import { ButtonColors, ButtonTypes } from '../../../../ui/gzButton/types';

interface GetVolumeDetailsResponse {
  data?: VolumeDetails;
  error?: number;
}

export class RecoveryManagerPopUp extends NetworkBasePopUp<GzRecoveryManagerWebComponent> {
  protected title = translateFn('Reveal encryption recovery key');
  protected extraDialogConfigs: Partial<GzDialog> = { isWindow: false };
  protected shouldCloseDialogAfterConfirm = false;
  protected popUpFooterButtons = [
    {
      type: ButtonTypes.FULL,
      id: 'confirmButton',
      color: ButtonColors.PRIMARY,
      label: translateFn('Reveal'),
    },
    {
      type: ButtonTypes.OUTLINE,
      id: 'closeButton',
      color: ButtonColors.SECONDARY,
      label: translateFn('Cancel'),
    },
  ];

  protected customSetup() {
    this.popUpContent = new GzRecoveryManagerWebComponent();
    this.popUpContent.hiddenPasswordField = Application.Main.getUser().source === Constants.Source.SOURCE_SSO_SAML;
  }

  protected async handleConfirmButtonClicked(event: CustomEvent): Promise<void> {
    const formValue = this.popUpContent.getFormValue();
    const protectorId = formValue.keyId;

    const getVolumeDetailsPromise = ConsoleRequest.createRequest<GetVolumeDetailsResponse>({
      dataProviderType: DataProviderType.CORE,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'EndpointDiskVolumeProtectors',
      method: 'getVolumeDetails',
      payload: {
        protectorId: protectorId,
        serviceId: Constants.ServiceTypes.EPS,
      },
    });

    let recoveryKeyPromise: Promise<string | { error: number }>;
    if (this.popUpContent.hiddenPasswordField) {
      recoveryKeyPromise = this.getRecoveryKey(protectorId);
    } else {
      recoveryKeyPromise = this.getRecoveryKey(protectorId, formValue.password);
    }

    const [volumeDetails, recoveryKey] = await Promise.all([getVolumeDetailsPromise, recoveryKeyPromise]);
    this.popUpFooterContent.setConfirmButtonLoadingState(false);

    this.popUpContent.revealInfo = {
      volumeDetails: volumeDetails.data,
      volumeDetailsErrorCode: volumeDetails.error,
      recoveryKey: typeof recoveryKey === 'string' ? recoveryKey : undefined,
      recoveryKeyErrorCode: typeof recoveryKey === 'object' ? recoveryKey.error : undefined,
    };

    return;
  }

  private async getRecoveryKey(protectorId: string, password?: string): Promise<string | { error: number }> {
    const payload = {
      protectorId: protectorId,
      serviceId: Constants.ServiceTypes.EPS,
    };

    if (password) {
      const encryptionKey = await ConsoleRequest.createRequest({
        dataProviderType: DataProviderType.CORE,
        dataProviderEntity: DataProviderEntity.SYSTEM,
        endpoint: 'SecurityManager',
        method: 'getAuthenticationKey',
      });

      const encryptionInstance = Ext.create('Application.lib.encryption');
      payload['encryptedPassword'] = encryptionInstance.encrypt(password, encryptionKey);
    }

    return ConsoleRequest.createRequest<string | { error: number }>({
      dataProviderType: DataProviderType.CORE,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'EndpointDiskVolumeProtectors',
      method: 'getRecoveryKey',
      payload: payload,
    });
  }
}
